// Day -> time slot picker over unified-pickup's available-slots (15-min
// increments). Groups slots by day, shows only days with availability, then the
// bookable times for the chosen day. Used for both new bookings and reschedules.
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { Slot, SlotsResponse } from "../lib/types";
import { Button, Spinner } from "./ui";

export function SlotPicker({
  pickupType = "default",
  onPick,
  confirming,
}: {
  pickupType?: string;
  onPick: (slot: Slot) => void;
  confirming?: boolean;
}) {
  const [weekOffset, setWeekOffset] = useState(0);
  const [openDay, setOpenDay] = useState<string | null>(null);
  const [selected, setSelected] = useState<Slot | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["slots", weekOffset, pickupType],
    queryFn: () => api.get<SlotsResponse>(`/api/pickups/slots?weekOffset=${weekOffset}&type=${pickupType}`),
  });

  // Group into days that have at least one available time.
  const days = useMemo(() => {
    const map = new Map<string, Slot[]>();
    for (const s of data?.slots || []) {
      if (!map.has(s.displayDate)) map.set(s.displayDate, []);
      map.get(s.displayDate)!.push(s);
    }
    return Array.from(map.entries())
      .map(([label, slots]) => ({ label, slots, available: slots.filter((s) => s.available) }))
      .filter((d) => d.available.length > 0);
  }, [data]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <button
          disabled={!data?.hasPreviousWeek}
          onClick={() => {
            setWeekOffset((w) => w - 1);
            setOpenDay(null);
          }}
          className="h-9 w-9 rounded-full text-navy disabled:text-ink-faint active:bg-navy-soft"
          aria-label="Previous week"
        >
          <i className="ph ph-caret-left text-lg" />
        </button>
        <span className="text-[13px] text-ink-muted">
          {data?.weekStart && data?.weekEnd ? `${data.weekStart} – ${data.weekEnd}` : "This week"}
        </span>
        <button
          disabled={!data?.hasNextWeek}
          onClick={() => {
            setWeekOffset((w) => w + 1);
            setOpenDay(null);
          }}
          className="h-9 w-9 rounded-full text-navy disabled:text-ink-faint active:bg-navy-soft"
          aria-label="Next week"
        >
          <i className="ph ph-caret-right text-lg" />
        </button>
      </div>

      {isLoading && <Spinner label="Loading times…" />}

      {!isLoading && days.length === 0 && (
        <p className="text-sm text-ink-subtle text-center py-6">
          No open pickup times this week. Try the next week.
        </p>
      )}

      <div className="flex flex-col gap-2">
        {days.map((d) => (
          <div key={d.label} className="border border-line rounded-xl overflow-hidden">
            <button
              onClick={() => setOpenDay(openDay === d.label ? null : d.label)}
              className="w-full flex items-center justify-between px-4 h-12 bg-surface active:bg-line-soft"
            >
              <span className="text-ink font-medium">{d.label}</span>
              <span className="text-[12px] text-ink-subtle">
                {d.available.length} time{d.available.length === 1 ? "" : "s"}
                <i className={`ph ph-caret-down ml-1 transition-transform ${openDay === d.label ? "rotate-180" : ""}`} />
              </span>
            </button>
            {openDay === d.label && (
              <div className="grid grid-cols-3 gap-2 p-3 bg-canvas">
                {d.available.map((s) => (
                  <button
                    key={s.date}
                    onClick={() => setSelected(s)}
                    className={`h-10 rounded-lg text-[13px] font-medium border ${
                      selected?.date === s.date
                        ? "bg-navy text-white border-navy"
                        : "bg-surface text-ink border-line active:bg-navy-soft"
                    }`}
                  >
                    {s.displayTime}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {selected && (
        <div className="sticky bottom-0 pt-2 bg-gradient-to-t from-canvas via-canvas">
          <Button className="w-full" disabled={confirming} onClick={() => onPick(selected)}>
            {confirming ? "Booking…" : `Confirm ${selected.displayDate.split(",")[0]} · ${selected.displayTime}`}
          </Button>
        </div>
      )}
    </div>
  );
}
